import React, { RefObject } from 'react';
import { connectDeep } from 'redux-utils';
import styled from 'styled-components';
import { back } from 'redux-first-router';

import fsc from 'fsc';
import App from 'models/app';
import i18n from 'i18n';
import { searchAllApps, searchPreview, searchDeveloperApps, searchDeveloperPreview } from 'actions/registry';
import { setSearchInput } from 'actions/gallery';
import xIcon from '../../icons/x-icon.svg';
import { BackLink } from '../../components/v2/Navigation/BackLink';
import { fontSemibold } from '../../themes';
import { ItemsPreview } from '../../components/v2/ItemsPreview';
import { VirtualizedAppList } from '../../components/v2/AppList/VirtualizedAppList';
import { VirtualizedClockList } from '../../components/v2/ClockList/VirtualizedClockList';
import { Transition } from '../../components/v2/Transition';
import { getType } from '../../utils.v2';
import { AppList } from '../../components/v2/AppList/AppList';
import { ClockList } from '../../components/v2/ClockList/ClockList';
import { SearchRecommendations } from '../../components/v2/SearchRecommendations';

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-evenly;
  border-bottom: 1px solid #E7E9EA;
`;

const HeaderLabel = styled.div`
  flex-grow: 1;
  text-align: center;
  padding: 10px;
  font-family: ${fontSemibold};
`;

const XMark = styled.div`
  padding-top: 10px;
  padding-right: 10px;
`;

const BaseInput = styled.input`
  &:focus {
    outline: 0;
  }

  &::-webkit-search-cancel-button {
    -webkit-appearance: none;
  }
  border: none;
  border-bottom: 1px solid #E7E9EA;
  padding: 20px 10px;
  width: 100%;
  font-size: 15px;
  box-sizing: border-box
  outline: none;
  appearance: none;
`;

export const FULL_VH = document.documentElement.clientHeight;

const Container = styled.div`
  overflow-y: auto;
  height: ${FULL_VH - 96}px;
  overflow-x: hidden;
  background: #FFFFFF;
`;

const mapStateToProps = (state: any) => {
  const {
    direction,
    searchInput: { text = '', developerName = '', developerProfileId, type }
  } = state.gallery;

  const {
    features: { flags },
  } = state.registry;

  const {
    search,
    searchPreview,
    searchDeveloperApps,
    searchDeveloperPreview,
    installed: {
      loading: installedLoading,
      error: installedError,
      entries: installedEntries = []
    }
  } = state.registry;

  const { isNeutral } =  getType(state.location);
  let searchLoading;
  const entries: string[] = [];
  let hasNextPage = false;

  let entriesKey: string;
  let searchPreviewAction;
  let searchAction;
  if (developerProfileId) {
    entriesKey = developerProfileId;
    searchPreviewAction = searchDeveloperPreview;
    searchAction = searchDeveloperApps;
  } else {
    entriesKey = text;
    searchPreviewAction = searchPreview;
    searchAction = search;
  }

  if (isNeutral) {
    const {
      [entriesKey]: {entries: previewEntries = []} = {},
    } = searchPreviewAction;
    searchLoading = searchPreviewAction.loading;
    entries.push(...previewEntries);
  } else {
    const {
      [`${entriesKey}-${type}`]: {
        entries: specificEntries = [],
        hasNextPage: specificHasNextPage = false
      } = {},
    } = searchAction;
    searchLoading = searchAction.loading;
    hasNextPage = specificHasNextPage;
    entries.push(...specificEntries);
  }

  const loading = installedEntries.length > 0 ? false : installedLoading;

  const error = installedError;

  return {
    loading: loading || (entries.length === 0 && searchLoading) || (!type && !isNeutral),
    error,
    text,
    flags,
    developerName,
    developerProfileId,
    apps: entries.map((appId: string) => App.selectFromState(appId, state)) as App[],
    hasNextPage,
    direction,
    location: state.location
  };
};

interface SearchArgs {
  text: string;
  startIndex?: number;
  type?: string;
}

interface SearchDeveloperAppsArgs {
  developerName?: string;
  developerProfileId: string;
  type?: string;
  startIndex?: number;
}

interface SearchDeveloperPreviewArgs {
  developerName?: string;
  developerProfileId: string;
}

const mapDispatchToProps = (dispatch: any) => ({
  search: ({ text, startIndex, type }: SearchArgs) =>
    dispatch(searchAllApps(type, text, startIndex)),
  searchPreview: ({ text }: SearchArgs) => dispatch(searchPreview(text)),
  searchDeveloperApps: ({ developerName, developerProfileId, type, startIndex }: SearchDeveloperAppsArgs) => dispatch(searchDeveloperApps(
    {
      developerName,
      developerProfileId,
      type,
      startIndex,
    }
  )),
  searchDeveloperPreview: ({ developerName, developerProfileId }: SearchDeveloperPreviewArgs) => dispatch(searchDeveloperPreview(
    developerName,
    developerProfileId,
  )),
  resetSearch: () => dispatch(setSearchInput({ text: '', developerName: '', developerProfileId: undefined, type: null }))
});

type ReduxProps = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;

interface SearchPageProps extends ReduxProps {
  text: string;
  developerName: string;
  developerProfileId: string;
  searchResults: App[];
}

interface SearchPageState {
  text: string;
  developerName: string;
  developerProfileId?: string;
}

class SearchPage extends React.Component<SearchPageProps, SearchPageState> {

  private readonly searchInput: RefObject<any>;

  constructor(props: SearchPageProps) {
    super(props);

    this.searchInput = React.createRef();
    this.state = {
      text: this.props.text,
      developerName: this.props.developerName,
      developerProfileId: this.props.developerProfileId,
    };
  }

  private get tilesEnabled() {
    return this.props.flags.enable_tiles
  }

  componentDidUpdate(prevProps: SearchPageProps, prevState: SearchPageState) {
    const { isNeutral } = getType(this.props.location);
    const { isNeutral: isNeutralPrevious } = getType(prevProps);

    // get search results when ViewAll buttons in search page are pressed
    if (this.props.location.type === 'SEARCH_ITEMS_V2' && prevProps.location.type === 'SEARCH_V2' &&
        this.props.location.kind === 'push') {
      this.getSearchResults();
    }

    // if a new text is added in the detailed search page, when navigating back to the
    // preview page the search results should be updated too
    if (this.props.location.kind === 'pop' && this.props.location.type === 'SEARCH_V2' &&
        prevProps.location.type === 'SEARCH_ITEMS_V2' && this.props.apps.length === 0 &&
        prevProps.apps.length != 0) {
      this.getSearchResults();
    }
  }

  componentWillMount() {
    const { isNeutral } = getType(this.props.location);
    if (!this.props.loading) {
      // Clear the search results when the search page was closed using the Android back button
      if (isNeutral && this.props.location.kind === 'push' && this.props.location.prev.type != 'APP_V2') {
        this.props.resetSearch();
        this.setState({text: '', developerName: '', developerProfileId: undefined });
      }
    }
  }

  // TODO: replace internal state with updating the Redux text state
  handleChange = (event: any) => {
    const text = event.target.value;
    this.setState({ text, developerName: '', developerProfileId: undefined });
  };

  handleSearch = (event: any) => {
    event.preventDefault();
    this.searchInput.current.blur();
    const { text } = this.state;

    const fscViewName = 'Search';
    fsc
      .view(fscViewName)
      .Tapped('Search Input')
      .withPayload({
        inputText: text
      })
      .send();

    this.getSearchResults();
  };

  render() {
    let backNavigate;
    const { isNeutral } = getType(this.props.location);

    if (isNeutral) {
      backNavigate = () => {
        this.props.resetSearch();
        back();
      };
    }

    return (
      <div>
        <HeaderContainer>
          <HeaderLabel>
            {i18n('page.Search.header')()}
          </HeaderLabel>
          <XMark>
            <BackLink icon={xIcon} backNavigate={backNavigate} />
          </XMark>
        </HeaderContainer>
        <div>
          <form onSubmit={this.handleSearch} action=".">
            <BaseInput
              ref={this.searchInput}
              id="search-input"
              aria-label="search-input"
              onChange={this.handleChange}
              placeholder={i18n('page.Search.placeholder')()}
              value={this.state.text}
            />
          </form>
          {this.renderLists()}
        </div>
      </div>
    );
  }

  private renderLists() {
    const { apps, search, searchDeveloperApps, hasNextPage, loading, error } = this.props;
    const { text, developerName, developerProfileId } = this.state;
    const { appType, isApp, isTile, isNeutral } = getType(this.props.location);

    if (loading || error) {
      return <Transition error={error} loading={loading} headerHeight={96} />
    }

    const hasNoResults = apps.length === 0;

    if ((!this.props.text && !this.props.developerProfileId) || hasNoResults) {
      return (
        <SearchRecommendations
          noResults={!!this.props.text && hasNoResults}
          onSearch={this.onRecommendationSearch}
        />
      );
    }

    if (!isNeutral) {
      let fetchMore;
      if (developerProfileId) {
        fetchMore = ({startIndex}: any) => searchDeveloperApps({
          developerName,
          developerProfileId,
          type: appType,
          startIndex,
        });
      } else {
        fetchMore = ({startIndex}: any) => search({
          startIndex,
          text,
          type: appType
        });
      }

      const virtualListProps = {
        apps,
        fetchMore: fetchMore,
        hasNextPage,
        navbarHeight: 96,
      };
      // a check added for small results to help in automation
      const isSmallResultSet = apps.length <= 10;
      return (
        <Container>
          {
            isApp || isTile ?
              isSmallResultSet ? <AppList apps={apps}/> :
                <VirtualizedAppList {...virtualListProps} /> :
              isSmallResultSet ? <ClockList apps={apps}/> :
                <VirtualizedClockList {...virtualListProps} />
          }
        </Container>
      );
    }
    const appItems: App[] = [];
    const tileItems: App[] = [];
    const clockItems: App[] = [];
    apps.forEach((app) => {
      if (app.isClock) {
        clockItems.push(app);
      } else if (app.isTile) {
        tileItems.push(app)
      } else {
        appItems.push(app);
      }
    });

    function createOnClickViewAll(type: string) {
      if (!developerProfileId) {
        return undefined;
      }
      return () => {
        searchDeveloperApps({developerName, developerProfileId, type});
      }
    }

    return (
      <Container>
        { !!appItems.length && <ItemsPreview
          id="view-all-apps"
          name={i18n('page.Home.apps')()}
          isClock={false}
          apps={appItems}
          path={`/v2/search/apps/${text || developerName}`}
          fscViewName={'Search'}
          inputText= {text || developerName}
          onClickViewAll={createOnClickViewAll("APP")}
        /> }
        { this.tilesEnabled && !!tileItems.length && <ItemsPreview
          id="view-all-tiles"
          name={i18n('page.Home.tiles')()}
          isClock={false}
          apps={tileItems}
          path={`/v2/search/tiles/${text}`}
          fscViewName={'Search'}
          inputText={text}
          onClickViewAll={createOnClickViewAll("TILE")}
        /> }
        { !!clockItems.length && <ItemsPreview
          id="view-all-clocks"
          name={i18n('page.Home.clocks')()}
          isClock={true}
          apps={clockItems}
          path={`/v2/search/clocks/${text || developerName}`}
          fscViewName={'Search'}
          inputText={text || developerName}
          onClickViewAll={createOnClickViewAll("CLOCK")}
        /> }
      </Container>
    );
  }

  onRecommendationSearch = (text: string) => {
    this.setState({ text, developerName: '', developerProfileId: undefined }, () => {
      this.getSearchResults();
    });
  }

  private getSearchResults() {
    const { text, developerName, developerProfileId } = this.state;
    const { appType, isNeutral } = getType(this.props.location);

    if (isNeutral) {
      if (developerProfileId) {
        this.props.searchDeveloperPreview({developerName, developerProfileId});
      } else {
        this.props.searchPreview({text});
      }
      return;
    }

    if (developerProfileId) {
      this.props.searchDeveloperApps({
        developerName,
        developerProfileId,
        type: appType,
      });
    } else {
      this.props.search({
        text,
        type: appType,
      });
    }
  }
}

export default connectDeep(mapStateToProps, mapDispatchToProps)(SearchPage);
